import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, TrendingUp, Building2, Lock, Award, Cpu,
  CheckCircle2, ArrowRight, Users, Headphones, BadgeCheck,
  Search, BarChart3, Database, Settings, Lightbulb
} from 'lucide-react';
import { servicesService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const iconMap = {
  strategy: BarChart3,
  wealth: TrendingUp,
  banking: Building2,
  compliance: Lock,
  education: Award,
  support: Cpu,
  audit: Search,
  conseil: Settings,
  core: Database,
  innovation: Lightbulb,
  shield: Shield,
};

function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await servicesService.getAll();
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingSpinner size="lg" text="Chargement des services..." />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-dark-900 via-primary-900 to-dark-900 overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-10" />

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Nos Services
            </h1>
            <p className="text-xl text-dark-300">
              Des solutions complètes pour accompagner votre transformation digitale bancaire
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid - 3 colonnes */}
      <section className="section bg-dark-50 py-16">
        <div className="container-custom">
          {services.length === 0 ? (
            <div className="text-center py-20">
              <Shield className="w-16 h-16 mx-auto mb-4 text-dark-300" />
              <h3 className="text-2xl font-semibold text-dark-900 mb-2">
                Aucun service disponible
              </h3>
              <p className="text-dark-500">
                Les services seront affichés ici une fois publiés depuis le panel admin.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Shield;

                return (
                  <motion.div
                    key={service.id || `service-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="bg-white rounded-xl p-6 h-full border border-dark-100 hover:shadow-lg transition-shadow">
                      {/* Icône */}
                      <div className="w-12 h-12 bg-dark-100 rounded-xl flex items-center justify-center mb-4">
                        <IconComponent className="w-6 h-6 text-dark-700" />
                      </div>

                      {/* Titre */}
                      <h3 className="text-lg font-semibold text-dark-900 mb-3">
                        {service.title}
                      </h3>

                      {/* Description */}
                      <p className="text-dark-500 text-sm mb-4 leading-relaxed">
                        {service.short_description}
                      </p>

                      {/* Features */}
                      {service.features && service.features.length > 0 && (
                        <ul className="space-y-2 mb-4">
                          {service.features.slice(0, 3).map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-dark-600">
                              <CheckCircle2 className="w-4 h-4 text-dark-400 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Lien */}
                      <Link
                        to={`/services/${service.slug}`}
                        className="inline-flex items-center text-dark-700 text-sm font-medium hover:text-primary-600 transition-colors"
                      >
                        En savoir plus
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Pourquoi choisir DDEC */}
      <section className="section bg-white py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-dark-900 mb-4">
              Pourquoi choisir DDEC ?
            </h2>
            <p className="text-dark-500 max-w-2xl mx-auto">
              Nous combinons expertise technique, connaissance du secteur bancaire et engagement pour votre réussite.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Expertise Reconnue */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-dark-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-dark-900 mb-3">
                Expertise Reconnue
              </h3>
              <p className="text-dark-500 text-sm leading-relaxed">
                Plus de 15 ans d'expérience dans la transformation digitale des institutions financières africaines.
              </p>
            </motion.div>

            {/* Support Continu */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-dark-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Headphones className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-dark-900 mb-3">
                Support Continu
              </h3>
              <p className="text-dark-500 text-sm leading-relaxed">
                Accompagnement 24/7 pour garantir le succès de vos projets et la satisfaction de vos équipes.
              </p>
            </motion.div>

            {/* Certifications */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-dark-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BadgeCheck className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-dark-900 mb-3">
                Certifications
              </h3>
              <p className="text-dark-500 text-sm leading-relaxed">
                Consultants certifiés sur les principales solutions Core Banking et normes internationales.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: 'rgb(26, 50, 84)' }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à démarrer votre projet ?
            </h2>
            <p className="text-gray-300 mb-8">
              Contactez-nous pour discuter de vos besoins et obtenir un devis personnalisé.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-gray-900 transition-colors font-medium"
              >
                Demande de devis
              </Link>
              <Link 
                to="/contact" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Contact
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default ServicesPage;